import React, { useState, useEffect, useMemo } from 'react';
import StorageService from '../../services/StorageService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../services/common.service';
import { API_URLS } from '../../constants/api-urls';
import './QADashboard.scss';

export default function QARequestsList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterParam = searchParams.get('filter') || 'all';
  
  const [rfqs, setRfqs] = useState([]);
  const [qaQuotationRequests, setQaQuotationRequests] = useState([]);
  const [rfqIdsWithInspection, setRfqIdsWithInspection] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(filterParam);

  useEffect(() => {
    setFilter(filterParam);
    fetchRFQs();
  }, [filterParam]);

  const fetchRFQs = async () => {
    try {
      setLoading(true);
      // Fetch "my inspections" first so we can exclude accepted RFQs before showing the list
      let inspectionRfqIds = new Set();
      try {
        const inspectionsResponse = await CommonService.get(API_URLS.AUCTION.GET_MY_INSPECTIONS);
        if (inspectionsResponse && inspectionsResponse.success && Array.isArray(inspectionsResponse.data)) {
          inspectionsResponse.data.forEach((ins) => {
            if (ins.rfq_id != null) {
              inspectionRfqIds.add(Number(ins.rfq_id));
              inspectionRfqIds.add(String(ins.rfq_id));
            }
          });
        }
      } catch (inspErr) {
        console.warn('Could not load my inspections for filter:', inspErr);
      }
      setRfqIdsWithInspection(inspectionRfqIds);

      const [rfqResponse, qaResponse] = await Promise.all([
        CommonService.get(API_URLS.AUCTION.GET_MY_INSPECTION_RFQS),
        CommonService.get(API_URLS.QA.INSPECTOR_MY_REQUESTS),
      ]);

      if (rfqResponse && rfqResponse.success) {
        setRfqs(rfqResponse.data || []);
      }
      if (qaResponse && qaResponse.success && Array.isArray(qaResponse.data)) {
        setQaQuotationRequests(qaResponse.data);
      }
    } catch (error) {
      console.error('Error fetching RFQs:', error);
      toast.error('Failed to load inspection requests');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const hasQuoted = (item) => Boolean(Number(item.has_quoted));

  const isContractManufacturerQa = (item) =>
    item?.source === 'quotation' && item?.context_source === 'contract_manufacturer';

  // RFQ statuses that mean buyer already accepted an inspector – hide these from requests list (show only in "My Inspections")
  const ACCEPTED_RFQ_STATUSES = ['inspector_selected', 'inspection_scheduled', 'completed'];

  // Exclude RFQs that already have an inspection (buyer accepted) – by inspection list and by RFQ status
  const requestsOnlyRFQs = useMemo(() => {
    return rfqs.filter(rfq => {
      const rfqId = rfq.rfq_id;
      if (rfqId == null) return true;
      if (rfqIdsWithInspection.has(Number(rfqId)) || rfqIdsWithInspection.has(String(rfqId))) return false;
      const status = (rfq.status || '').toLowerCase();
      if (ACCEPTED_RFQ_STATUSES.some(s => s === status)) return false;
      return true;
    });
  }, [rfqs, rfqIdsWithInspection]);

  // Combined list: auction RFQs + quotation-based QA requests (buyer-submitted QA requests)
  const allRequests = useMemo(() => {
    const auctionList = requestsOnlyRFQs.map(r => ({ ...r, source: 'auction', _key: `auction-${r.rfq_id}` }));
    const quotationList = (qaQuotationRequests || []).map(q => ({ ...q, source: 'quotation', _key: `quotation-${q.qa_request_id}` }));
    return [...auctionList, ...quotationList];
  }, [requestsOnlyRFQs, qaQuotationRequests]);

  const getStatusBadge = (item) => {
    if (hasQuoted(item)) {
      return <span className="badge badge-quoted">Quoted</span>;
    }
    return <span className="badge badge-pending">New Request</span>;
  };

  const isAcceptedRFQ = (rfq) => {
    if (!rfq) return false;
    const rfqId = rfq.rfq_id;
    if (rfqId != null && (rfqIdsWithInspection.has(Number(rfqId)) || rfqIdsWithInspection.has(String(rfqId)))) return true;
    const status = (rfq.status || '').toLowerCase();
    if (['inspector_selected', 'inspection_scheduled', 'completed'].includes(status)) return true;
    return false;
  };

  const filteredRFQs = useMemo(() => {
    return allRequests.filter(item => {
      if (item.source === 'quotation') {
        const quoted = hasQuoted(item);
        if (filter === 'pending') return !quoted;
        if (filter === 'quoted') return quoted;
        return true;
      }
      const quoted = hasQuoted(item);
      if (filter === 'pending') {
        if (isAcceptedRFQ(item)) return false;
        return !quoted;
      }
      if (filter === 'quoted') return quoted;
      return true;
    });
  }, [allRequests, filter, rfqIdsWithInspection]);

  if (loading) {
    return (
      <div className="qa-dashboard loading">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="qa-dashboard">
      <div className="container-fluid">
        {/* Header */}
        <div className="page-header">
          <button className="back-btn" onClick={() => navigate('/auction/qa/dashboard')}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Dashboard
          </button>
          <div className="header-content">
            <h1>Inspection Requests</h1>
            <p>
              View and respond to inspection requests from buyers — including auction, buyer–seller, and contract
              manufacturing QA requests assigned to you.
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="filter-tabs">
            <button
              className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
              onClick={() => {
                setFilter('all');
                navigate('/auction/qa/dashboard/requests?filter=all');
              }}
            >
              All Requests ({allRequests.length})
            </button>
            <button
              className={`filter-tab ${filter === 'pending' ? 'active' : ''}`}
              onClick={() => {
                setFilter('pending');
                navigate('/auction/qa/dashboard/requests?filter=pending');
              }}
            >
              New Requests ({allRequests.filter(r => !hasQuoted(r)).length})
            </button>
            <button
              className={`filter-tab ${filter === 'quoted' ? 'active' : ''}`}
              onClick={() => {
                setFilter('quoted');
                navigate('/auction/qa/dashboard/requests?filter=quoted');
              }}
            >
              Quoted ({allRequests.filter(r => hasQuoted(r)).length})
            </button>
          </div>
        </div>

        {/* Requests List (auction RFQs + quotation QA requests) */}
        <div className="rfqs-list">
          {filteredRFQs.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                  <path d="M32 8C18.745 8 8 18.745 8 32C8 45.255 18.745 56 32 56C45.255 56 56 45.255 56 32C56 18.745 45.255 8 32 8ZM32 52C21.514 52 12 42.486 12 32C12 21.514 21.514 12 32 12C42.486 12 52 21.514 52 32C52 42.486 42.486 52 32 52Z" fill="currentColor"/>
                  <path d="M32 20V32M32 36H32.02" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>No Inspection Requests</h3>
              <p>You don't have any inspection requests at the moment.</p>
            </div>
          ) : (
            filteredRFQs.map((item) => item.source === 'quotation' ? (
              <div key={item._key} className="rfq-card">
                <div className="rfq-card-header">
                  <div className="rfq-info">
                    <div className="rfq-id">
                      {item.qa_request_number || `QA #${item.qa_request_id}`}
                      {isContractManufacturerQa(item) ? (
                        <span className="badge bg-info text-dark ms-2" title="Contract manufacturing RFQ / quotation">
                          Contract manufacturing
                        </span>
                      ) : null}
                    </div>
                    {getStatusBadge(item)}
                  </div>
                  <div className="rfq-date">Requested: {formatDate(item.created_at)}</div>
                </div>
                <div className="rfq-card-body">
                  <div className="product-section">
                    <div className="product-image">
                      <div className="placeholder-image">{isContractManufacturerQa(item) ? 'CM' : 'QA'}</div>
                    </div>
                    <div className="product-details">
                      <h3 className="product-name">{(item.scope_logistics && item.scope_logistics.product_name) || 'Quality Inspection Request'}</h3>
                      <div className="product-meta">
                        <span className="category">
                          {isContractManufacturerQa(item) ? 'Contract manufacturing · quotation flow' : 'Buyer–seller · quotation flow'}
                        </span>
                        {isContractManufacturerQa(item) && item.cm_rfq_id != null && item.cm_quotation_id != null ? (
                          <span className="category ms-2">
                            CM RFQ #{String(item.cm_rfq_id).padStart(5, '0')} · Quote #{String(item.cm_quotation_id).padStart(6, '0')}
                          </span>
                        ) : null}
                      </div>
                      {isContractManufacturerQa(item) && item.cm_manufacturer_name ? (
                        <p className="product-description mb-0 mt-1">
                          <strong>Manufacturer:</strong> {item.cm_manufacturer_name}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="inspection-details">
                    <div className="detail-item">
                      <div className="detail-icon">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M10 1C6.24 1 4 3.24 4 6C4 10.5 10 17 10 17C10 17 16 10.5 16 6C16 3.24 13.76 1 10 1ZM10 8.25C8.76 8.25 7.75 7.24 7.75 6C7.75 4.76 8.76 3.75 10 3.75C11.24 3.75 12.25 4.76 12.25 6C12.25 7.24 11.24 8.25 10 8.25Z" fill="currentColor"/>
                        </svg>
                      </div>
                      <div className="detail-content">
                        <div className="detail-label">Inspection Location</div>
                        <div className="detail-value">
                          {item.inspection_location_address || '—'}
                          <br />
                          {[item.inspection_location_city, item.inspection_location_state].filter(Boolean).join(', ') || '—'}
                        </div>
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-icon">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <rect x="3" y="4" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                      </div>
                      <div className="detail-content">
                        <div className="detail-label">Preferred Schedule</div>
                        <div className="detail-value">
                          {item.preferred_start_date ? formatDate(item.preferred_start_date) : '—'}
                          {item.preferred_end_date ? ` to ${formatDate(item.preferred_end_date)}` : ''}
                        </div>
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-icon">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M10 2C5.58 2 2 5.58 2 10C2 14.42 5.58 18 10 18C14.42 18 18 14.42 18 10C18 5.58 14.42 2 10 2ZM11 14H9V12H11V14ZM11 10H9V6H11V10Z" fill="currentColor"/>
                        </svg>
                      </div>
                      <div className="detail-content">
                        <div className="detail-label">Buyer</div>
                        <div className="detail-value">
                          {item.buyer_company || item.buyer_name || 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="rfq-card-footer">
                  {hasQuoted(item) ? (
                    <button
                      className="btn-view-quotation"
                      onClick={() => navigate(`/auction/qa/quotation-request/${item.qa_request_id}`)}
                    >
                      View Quotation
                    </button>
                  ) : (
                    <button
                      className="btn-submit-quotation"
                      onClick={() => navigate(`/auction/qa/quotation-request/${item.qa_request_id}`)}
                    >
                      Submit Quotation
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M18 2L9 11M18 2L12 18L9 11M18 2L2 8L9 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div key={item._key} className="rfq-card">
                <div className="rfq-card-header">
                  <div className="rfq-info">
                    <div className="rfq-id">RFQ #{item.rfq_number}</div>
                    {getStatusBadge(item)}
                  </div>
                  <div className="rfq-date">Requested: {formatDate(item.created_at)}</div>
                </div>
                <div className="rfq-card-body">
                  <div className="product-section">
                    <div className="product-image">
                      {item.images && Array.isArray(item.images) && item.images.length > 0 ? (
                        <img src={item.images[0]} alt={item.product_name} />
                      ) : (
                        <div className="placeholder-image">No Image</div>
                      )}
                    </div>
                    <div className="product-details">
                      <h3 className="product-name">{item.product_name}</h3>
                      <div className="product-meta">
                        <span className="category">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M2 4H14M2 4V12C2 12.5304 2.21071 13.0391 2.58579 13.4142C2.96086 13.7893 3.46957 14 4 14H12C12.5304 14 13.0391 13.7893 13.4142 13.4142C13.7893 13.0391 14 12.5304 14 12V4M2 4L2 2C2 1.46957 2.21071 0.96086 2.58579 0.585786C2.96086 0.210714 3.46957 0 4 0H6M14 4V2C14 1.46957 13.7893 0.96086 13.4142 0.585786C13.0391 0.210714 12.5304 0 12 0H10M6 0V4M6 0H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                          {item.category_name || 'N/A'}
                        </span>
                        <span className="bid-amount">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 1V15M1 8H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                          Bid: {formatCurrency(item.winning_bid_amount)}
                        </span>
                      </div>
                      {item.product_description && (
                        <p className="product-description">{item.product_description.substring(0, 150)}...</p>
                      )}
                    </div>
                  </div>
                  <div className="inspection-details">
                    <div className="detail-item">
                      <div className="detail-icon">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M10 1C6.24 1 4 3.24 4 6C4 10.5 10 17 10 17C10 17 16 10.5 16 6C16 3.24 13.76 1 10 1ZM10 8.25C8.76 8.25 7.75 7.24 7.75 6C7.75 4.76 8.76 3.75 10 3.75C11.24 3.75 12.25 4.76 12.25 6C12.25 7.24 11.24 8.25 10 8.25Z" fill="currentColor"/>
                        </svg>
                      </div>
                      <div className="detail-content">
                        <div className="detail-label">Inspection Location</div>
                        <div className="detail-value">
                          {item.inspection_location_address}
                          <br />
                          {item.inspection_location_city}, {item.inspection_location_state}
                        </div>
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-icon">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <rect x="3" y="4" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                      </div>
                      <div className="detail-content">
                        <div className="detail-label">Preferred Schedule</div>
                        <div className="detail-value">
                          {formatDate(item.preferred_date)} at {item.preferred_time}
                        </div>
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-icon">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M10 2C5.58 2 2 5.58 2 10C2 14.42 5.58 18 10 18C14.42 18 18 14.42 18 10C18 5.58 14.42 2 10 2ZM11 14H9V12H11V14ZM11 10H9V6H11V10Z" fill="currentColor"/>
                        </svg>
                      </div>
                      <div className="detail-content">
                        <div className="detail-label">Buyer</div>
                        <div className="detail-value">
                          {item.buyer_company || item.buyer_name || 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="rfq-card-footer">
                  {hasQuoted(item) ? (
                    <button
                      className="btn-view-quotation"
                      onClick={() => navigate(`/auction/inspection-rfq/${item.rfq_id}`)}
                    >
                      View Quotation
                    </button>
                  ) : (
                    <button
                      className="btn-submit-quotation"
                      onClick={() => navigate(`/auction/inspection-rfq/${item.rfq_id}`)}
                    >
                      Submit Quotation
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M18 2L9 11M18 2L12 18L9 11M18 2L2 8L9 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
